import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import useGetPostedInvoices from '../hooks/useGetPostedInvoices';
import useGetPostedInvoicesIdList, {
    getPreInvoicedIdsRequestParams,
} from '../hooks/useGetPostedInvoicesIdList';
import useLocalStorageState from '../../utils/useLocalStorageState';
import { COLORS } from '../../utils/Application_Constants';
import useInvoicingFilters from '../hooks/Invoicing/useFiltersInvoiceManagementFilters';
import FilterDrawer from '../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../Shared/Components/SearchInputWithDropdown';
import {
    managementSearchOptions,
    formatFiltersForManagementApi,
} from '../Configurations/invoicingConfig';
import PageContainer from '../Components/PageContainer';
import Tabs from '../../Shared/Components/Tab/Tabs';
import Tab from '../../Shared/Components/Tab/Tab';
import TabPanel from '../../Shared/Components/Tab/TabPanel';
import {
    InvoicingSelectionContextProvider,
    useInvoicingSelectionContext,
} from '../Contexts/InvoicingSelectionContext';
import ListViewTableHeaderRow from '../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import InvoiceItemContainer from '../Components/Invoicing/InvoiceManagement/InvoiceItemContainer';
import PaginationBar from '../../Shared/Components/PaginationBar';
import { MIN_COLUMN_WIDTH } from '../Components/Invoicing/styles';
import adaptApiInvoiceToUi from '../Components/Invoicing/InvoiceManagement/AdaptApiInvoiceToUi';
import useAdminAuthContext from '../hooks/useAdminAuthContext';
import PreInvoiceConfirmationModal from '../Components/Invoicing/InvoiceManagement/PreInvoiceConfirmationModal';
import PreInvoiceStatusErrorsBanner from '../Components/Invoicing/InvoiceManagement/PreInvoiceStatusErrorsBanner';

const DEFAULT_SORT_FIELD = 'invoiceDate';
const DEFAULT_SORT_DIRECTION = 'desc';
const PAGE_NAME = 'invoiceManagement';
const PAGE_SIZE = 20;
const minWidth = MIN_COLUMN_WIDTH;

const TABLE_HEADERS = [
    { label: 'Client', key: 'client', size: 1.7, sx: { minWidth } },
    {
        label: (
            <>
                Customer
                <br />
                Number
            </>
        ),
        title: 'Customer Number',
        key: 'customerNumber',
        size: 0.6,
        sx: { minWidth },
    },
    { label: 'Invoice #', key: 'invoiceNumber', size: 0.8, sx: { minWidth } },
    { label: 'Clinician', key: 'clinician', size: 1.2, sx: { minWidth } },
    { label: 'Booking', key: 'bookingId', size: 0.8, sx: { minWidth }, sortable: false },
    {
        label: (
            <>
                Invoice
                <br />
                Date
            </>
        ),
        title: 'Invoice Date',
        key: 'invoiceDate',
        size: 0.8,
        sx: { minWidth },
    },
    {
        label: (
            <>
                Posted
                <br />
                Date
            </>
        ),
        title: 'Posted Date',
        key: 'postedDate',
        size: 0.8,
        sx: { minWidth },
    },
    { label: 'Status', key: 'status', size: 1, sx: { minWidth: 130 } },
    { label: 'Amount', key: 'amount', size: 0.8, sx: { minWidth } },
    { label: 'Collector', key: 'collector', size: 1, sx: { minWidth } },
    { label: 'Type', key: 'type', size: 0.5, sx: { minWidth } },
    { label: 'Actions', key: 'actions', size: 0.5, sortable: false },
];

function InvoiceManagementPage() {
    const [disableSelection, setDisableSelection] = useState(true);
    const [showDisabledAlert, setShowDisabledAlert] = useState(false);
    const [scrollWidth, setScrollWidth] = useState('100%');
    const tableContentRef = useRef(null);
    const scrollbarRef = useRef(null);
    const {
        selectStatus,
        handleSelectAllBookings,
        selectedBookingsCount,
        handleInitializeNewBookings,
        handleReset: resetSelection,
    } = useInvoicingSelectionContext();

    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(
        `${PAGE_NAME}SortField`,
        DEFAULT_SORT_FIELD
    );
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${PAGE_NAME}SortDirection`,
        DEFAULT_SORT_DIRECTION
    );
    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${PAGE_NAME}Search`, {
        searchWord: '',
        searchColumns: '',
    });
    const { state: tabValue, setValue: setTabValue } = useLocalStorageState(
        `${PAGE_NAME}SelectedTab`,
        'pre-invoiced'
    );
    const [showPreInvoiceConfirmationModal, setShowPreInvoiceConfirmationModal] = useState(false);
    const [preInvoiceAction, setPreInvoiceAction] = useState('invoice');
    const [preInvoiceErrorBannerState, setPreInvoiceErrorBannerState] = useState({
        show: false,
        type: '',
        message: '',
        errors: [],
    });

    const handleAbandonSelected = () => {
        setPreInvoiceAction('abandon');
        setShowPreInvoiceConfirmationModal(true);
    };

    const handleInvoiceSelected = () => {
        setPreInvoiceAction('invoice');
        setShowPreInvoiceConfirmationModal(true);
    };

    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useInvoicingFilters(PAGE_NAME, tabValue);

    const [page, setPage] = useState(1);
    const offset = (page - 1) * PAGE_SIZE;
    const handlePageChange = (_, newPage) => setPage(newPage);
    const { isInvoicingSystemAdminUser } = useAdminAuthContext();

    const requestParams = useMemo(() => {
        const formattedFilters = formatFiltersForManagementApi(filters, filtersSetup) || {};

        const searchParams = {};
        if (searchData?.searchWord?.length >= 3) {
            searchParams.SearchWord = searchData?.searchWord;
            searchParams.SearchColumns = searchData.searchColumns;
        }

        const sortParams = {};
        if (sortBy) {
            const dir = String(sortDirection || DEFAULT_SORT_DIRECTION).toUpperCase();
            sortParams.orderBy = sortBy;
            sortParams.dir = dir;
        }

        const base = {
            offset,
            pageSize: PAGE_SIZE,
            isPreInvoice: tabValue === 'pre-invoiced',
        };

        return { ...base, ...formattedFilters, ...searchParams, ...sortParams };
    }, [offset, tabValue, sortBy, sortDirection, filters, filtersSetup, searchData]);

    const { data: invoices, isLoading, isFetching, refetch } = useGetPostedInvoices(requestParams);

    const uiInvoices = useMemo(() => {
        const rows = Array.isArray(invoices?.data) ? invoices.data : [];
        return rows
            .map((r) =>
                adaptApiInvoiceToUi(r, { forcePreInvoicedLabel: tabValue === 'pre-invoiced' })
            )
            .filter(Boolean);
    }, [invoices, tabValue]);

    const totalCount = invoices?.totalCount ?? 0;
    const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

    const { isFetching: isFetchingIds, refetch: fetchIds } = useGetPostedInvoicesIdList(
        getPreInvoicedIdsRequestParams(requestParams),
        {
            enabled: false,
            onSuccess: ({ data: idsData }) => {
                const invoiceIds = idsData?.[0]?.invoiceIds || [];

                if (invoiceIds.length === 0) return;

                const allBookings = invoiceIds.map((id) => ({ bookingId: id }));
                handleInitializeNewBookings(allBookings);
                setDisableSelection(false);
            },
            onError: () => {
                setDisableSelection(true);
                setShowDisabledAlert(true);
            },
        }
    );

    const fetchAllSelectableIdsAndResetSelection = useCallback(() => {
        setShowDisabledAlert(false);
        resetSelection();
        if (tabValue === 'pre-invoiced') {
            const hasNoStatusFilter = !filters?.status?.length;
            const includesPreInvoiced = filters?.status?.includes('PreInvoiced');
            const shouldFetchIds = hasNoStatusFilter || includesPreInvoiced;
            if (shouldFetchIds) fetchIds();
        }
    }, [filters, searchData, tabValue]);

    // When the user changes filters/search/tab, start from page 1
    useEffect(() => {
        setPage(1);

        fetchAllSelectableIdsAndResetSelection();
    }, [fetchAllSelectableIdsAndResetSelection]);

    // Sync scrollbars and handle resize
    useEffect(() => {
        const tableContent = tableContentRef.current;
        const scrollbar = scrollbarRef.current;

        if (!tableContent || !scrollbar) return;

        const handleTableScroll = () => {
            scrollbar.scrollLeft = tableContent.scrollLeft;
        };

        const handleScrollbarScroll = () => {
            tableContent.scrollLeft = scrollbar.scrollLeft;
        };

        const handleResize = () => {
            if (tableContent) {
                setScrollWidth(`${tableContent.scrollWidth}px`);
            }
        };

        tableContent.addEventListener('scroll', handleTableScroll);
        scrollbar.addEventListener('scroll', handleScrollbarScroll);
        window.addEventListener('resize', handleResize);

        // Update scroll width when content changes
        if (tableContentRef.current) {
            setScrollWidth(`${tableContentRef.current.scrollWidth}px`);
        }

        return () => {
            tableContent.removeEventListener('scroll', handleTableScroll);
            scrollbar.removeEventListener('scroll', handleScrollbarScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [uiInvoices]);

    const canInvoice =
        tabValue === 'pre-invoiced' && isInvoicingSystemAdminUser && !disableSelection;

    return (
        <PageContainer>
            <Typography variant="h3" sx={{ fontSize: 22 }}>
                Invoice Management
            </Typography>

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                mt="12px"
            >
                <FilterDrawer
                    key={tabValue}
                    isLoading={isLoadingFilterConfig || isLoading}
                    config={filtersSetup}
                    onChange={handleApplyFilter}
                    storedValues={filters}
                />

                <SearchInputWithDropdown
                    isLoading={isLoading}
                    options={managementSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumns}
                    onSubmit={({ search, searchKey }) => {
                        setSearch({ searchWord: search, searchColumns: searchKey });
                    }}
                />
            </Stack>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {appliedFiltersDescription}
            </Typography>

            {preInvoiceErrorBannerState.show && (
                <PreInvoiceStatusErrorsBanner
                    type={preInvoiceErrorBannerState.type}
                    message={preInvoiceErrorBannerState.message}
                    errors={preInvoiceErrorBannerState.errors}
                    onClose={() => {
                        setPreInvoiceErrorBannerState({
                            show: false,
                            type: '',
                            message: '',
                            errors: [],
                        });
                    }}
                />
            )}

            <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
                <Tab
                    label={`Pre-Invoiced${
                        tabValue === 'pre-invoiced' && totalCount !== null && !isFetching
                            ? ` (${totalCount})`
                            : ''
                    }`}
                    index={0}
                    value="pre-invoiced"
                />
                <Tab
                    label={`Invoiced${
                        tabValue === 'invoiced' && totalCount !== null && !isFetching
                            ? ` (${totalCount})`
                            : ''
                    }`}
                    index={1}
                    value="invoiced"
                />
            </Tabs>

            <TabPanel>
                {!isLoading && uiInvoices?.length > 0 ? (
                    <Box
                        sx={{ py: '22px', px: '26px', borderRadius: '10px', position: 'relative' }}
                    >
                        {tabValue === 'pre-invoiced' && (
                            <Alert severity="info" sx={{ mb: '15px', fontSize: '12px' }}>
                                You are viewing pre-invoices. These have not been formally invoiced
                                to the client.
                            </Alert>
                        )}

                        <Box mt={0} mb="15px">
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                handlePageChange={handlePageChange}
                            />
                        </Box>

                        {showDisabledAlert && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setShowDisabledAlert(false)}
                            >
                                Selection has been disabled due to an error fetching selectable
                                items. Please refresh the page or adjust your filters.
                            </Alert>
                        )}

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb="20px"
                        >
                            <Typography color={COLORS.LT_MIDNIGHT}>
                                {tabValue === 'pre-invoiced' ? 'Pre-Invoices' : 'Invoices'}
                            </Typography>

                            {canInvoice && (
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Button
                                        variant="lt-admin-outlined"
                                        color="error"
                                        size="small"
                                        disabled={selectedBookingsCount === 0}
                                        onClick={handleAbandonSelected}
                                    >
                                        Abandon selected ({selectedBookingsCount})
                                    </Button>
                                    <Button
                                        variant="lt-admin-contained"
                                        color="primary"
                                        size="small"
                                        disabled={selectedBookingsCount === 0}
                                        onClick={handleInvoiceSelected}
                                    >
                                        Invoice selected ({selectedBookingsCount})
                                    </Button>
                                </Stack>
                            )}
                        </Stack>

                        <Box
                            ref={tableContentRef}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                position: 'relative',
                                // Hide the scrollbar of the actual content
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                scrollbarWidth: 'none', // For Firefox
                                msOverflowStyle: 'none', // For IE and Edge
                            }}
                        >
                            <ListViewTableHeaderRow
                                selectStatus={canInvoice ? selectStatus : undefined}
                                onSelectAll={canInvoice ? handleSelectAllBookings : undefined}
                                selectColumnProps={{
                                    sx: {
                                        justifyContent: 'flex-end',
                                        minWidth: '90px',
                                        pr: '2.5px !important',
                                    },
                                }}
                                columns={TABLE_HEADERS}
                                onSortChange={handleSortChange}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                shouldCleanSort
                                sortIconPosition="left"
                                prependComponent={
                                    tabValue === 'pre-invoiced' ? null : (
                                        <Box
                                            sx={{
                                                minWidth: '90px',
                                                pr: '2.5px !important',
                                                backgroundColor: COLORS.LT_MIDNIGHTBG1,
                                                borderRadius: '8px 0 0 8px',
                                            }}
                                        />
                                    )
                                }
                            />

                            {!isFetching &&
                                !isFetchingIds &&
                                uiInvoices?.map((invoice) => (
                                    <InvoiceItemContainer
                                        key={invoice.bookingId}
                                        invoice={invoice}
                                        disableSelection={disableSelection}
                                    />
                                ))}

                            {(isFetching || isFetchingIds) && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '20px',
                                    }}
                                    aria-label="loading-overlay"
                                >
                                    <CircularProgress size={28} />
                                </Box>
                            )}
                        </Box>

                        {/* Static horizontal scrollbar at the bottom */}
                        <Box
                            ref={scrollbarRef}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                position: 'sticky',
                                bottom: 0,
                                left: 0,
                                zIndex: 10,
                                backgroundColor: 'white',
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                // Force scrollbar to always be visible
                                '&::-webkit-scrollbar': {
                                    height: '10px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#888',
                                    borderRadius: '6px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#555',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    width: scrollWidth,
                                }}
                            />
                        </Box>

                        {showPreInvoiceConfirmationModal && (
                            <PreInvoiceConfirmationModal
                                open
                                onClose={() => setShowPreInvoiceConfirmationModal(false)}
                                action={preInvoiceAction}
                                onSuccess={({ message, type, errors }) => {
                                    setPreInvoiceErrorBannerState({
                                        show: true,
                                        type,
                                        message,
                                        errors,
                                    });
                                    refetch();
                                    fetchAllSelectableIdsAndResetSelection();
                                }}
                            />
                        )}
                    </Box>
                ) : (
                    <Typography variant="body1" component="p" p={2}>
                        {isLoading ? <CircularProgress /> : 'No results found'}
                    </Typography>
                )}
            </TabPanel>

            {uiInvoices?.length > 0 && (
                <Box mt="15px">
                    <PaginationBar
                        page={page}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                        isLoading={isFetching}
                    />
                </Box>
            )}
        </PageContainer>
    );
}

export default function InvoiceManagementPageWithContext() {
    return (
        <InvoicingSelectionContextProvider bookings={[]} totalCount={[]}>
            <InvoiceManagementPage />
        </InvoicingSelectionContextProvider>
    );
}
